import { computed } from 'vue'
import type { Ref } from 'vue'
import { storeToRefs } from 'pinia'
import uniqBy from 'lodash.uniqby'
import { useComponents } from '@/store/modules/App/component'
import { useSpace } from '@/store/modules/App/space'
import { Permissions } from '@/types'
import type { ComponentGroup, Component } from '@/types'

type useBlockLibraryCustomRolePermissionsType = {
  allowedComponents: Ref<Component[]>
  allowedRootComponents: Ref<Component[]>
  allowedComponentFolders: Ref<ComponentGroup[]>
  allowedFoldersByEditorPermissions: Ref<ComponentGroup[]>
  allowedGroupedComponents: Ref<Component[]>
}

/**
 * @function filterByPermissions
 * @description
 * Filters an array of `Component` or `ComponentGroup` items based on user permissions.
 * It checks allowed and blocked permission keys to determine which items should be included in the result.
 * Optionally, a cross-check permission key can be provided to enforce stricter filtering.
 *
 * @param items - The array of items (`Component[]` or `ComponentGroup[]`) to filter.
 * @param allowPermissionKey - The permission key that specifies which items are allowed.
 * @param blockPermissionKey - The permission key that specifies which items are blocked.
 * @param crossCheckPermissionKey - (Optional) An additional permission key for cross-checking permissions.
 * @param requireExplicitPermission - (Optional) If true, only returns items that are explicitly in the allowed list.
 * @returns The filtered array of items, based on the user's permissions.
 */

function filterByPermissions<T extends { id: number }>(
  items: T[],
  allowPermissionKey: 'managed_component_ids' | 'managed_component_group_uuids',
  blockPermissionKey:
    | 'blocked_manage_component_ids'
    | 'blocked_manage_component_group_uuids',
  crossCheckPermissionKey?:
    | 'managed_component_ids'
    | 'managed_component_group_uuids',
  requireExplicitPermission = false,
): T[] {
  const spaceStore = useSpace()
  const permissions = spaceStore.userPermissions

  const allowedIds = permissions[allowPermissionKey] || []
  const blockedIds = permissions[blockPermissionKey] || []

  if (crossCheckPermissionKey) {
    const crossCheckIds = permissions[crossCheckPermissionKey] || []
    // If the cross-check permission has values and current permission doesn't, return empty
    if (
      crossCheckIds.length > 0 &&
      allowedIds.length === 0 &&
      blockedIds.length === 0
    ) {
      return []
    }
  }

  function getItemIdentifier(item: T): string | number {
    if ('uuid' in item && typeof item.uuid === 'string') {
      return item.uuid
    }
    return item.id
  }

  if (allowedIds.length > 0 && blockedIds.length > 0) {
    return items.filter((item) => {
      const identifier = getItemIdentifier(item)
      return (
        allowedIds.some(
          (allowedId) => String(allowedId) === String(identifier),
        ) &&
        !blockedIds.some(
          (blockedId) => String(blockedId) === String(identifier),
        )
      )
    })
  }

  if (allowedIds.length > 0) {
    return items.filter((item) => {
      const identifier = getItemIdentifier(item)
      return allowedIds.some(
        (allowedId) => String(allowedId) === String(identifier),
      )
    })
  }

  if (blockedIds.length > 0) {
    return items.filter((item) => {
      const identifier = getItemIdentifier(item)
      return !blockedIds.some(
        (blockedId) => String(blockedId) === String(identifier),
      )
    })
  }

  if (requireExplicitPermission && allowedIds.length === 0) {
    return []
  }

  return items || []
}

function findAllSubfolders(
  parentIds: string[],
  allItems: ComponentGroup[],
): string[] {
  const subfolderIds: string[] = []

  const itemsWithParent = allItems.filter((item) => {
    if ('parent_uuid' in item) {
      const parentUuid = 'parent_uuid' in item ? item.parent_uuid : null

      return parentIds?.some((parentId) => {
        // we check for parentUuid !== item.uuid because there might be cases where the folder uuid
        // is the same as their parent_uuid  which is wrong, so we need to avoid (circular reference)
        return parentUuid === parentId && parentUuid !== item.uuid
      })
    }
    return false
  })

  for (const item of itemsWithParent) {
    const identifier = item.uuid
    subfolderIds.push(identifier)

    // Recursively find subfolders of this subfolder
    const deeperSubfolders = findAllSubfolders([identifier], allItems)
    subfolderIds.push(...deeperSubfolders)
  }

  return subfolderIds
}

function combineAndFilterComponents(
  fromFolders: Component[],
  fromPermissions: Component[],
  hasBlockedIds: boolean,
): Component[] {
  if (!hasBlockedIds) {
    return uniqBy([...fromFolders, ...fromPermissions], 'id')
  }

  // When there are blocked IDs, only include components that exist in both arrays
  return fromFolders.filter((component) =>
    fromPermissions.some(
      (allowed) => String(allowed.id) === String(component.id),
    ),
  )
}

export function useBlockLibraryCustomRolePermissions(): useBlockLibraryCustomRolePermissionsType {
  const spaceStore = useSpace()
  const permissions = spaceStore.userPermissions
  const allowedIds = permissions.managed_component_group_uuids || []
  const blockedIds = permissions.blocked_manage_component_group_uuids || []

  // We need to use the permissions directly to not have a circular dependency with the permissions plugin
  const canApplyToBlockSubfolders =
    spaceStore?.userPermissions?.permissions?.includes(
      Permissions.ApplyToBlockSubfolders,
    ) &&
    (allowedIds.length > 0 || blockedIds.length > 0)

  const componentsStore = useComponents()

  const {
    getComponents: components,
    getRootComponents: rootComponents,
    getComponentGroups: componentFolders,
    getGroupedComponents: groupedComponents,
  } = storeToRefs(componentsStore)

  const allowedComponents = computed((): Component[] => {
    const allowedComponentsFromPermissions = filterByPermissions(
      components.value,
      'managed_component_ids',
      'blocked_manage_component_ids',
      'managed_component_group_uuids',
    )

    if (canApplyToBlockSubfolders) {
      const allowedComponentsFromFolders = components.value.filter(
        (component) =>
          allowedComponentFolders.value.some(
            (folder) => component.component_group_uuid === folder.uuid,
          ),
      )

      const combinedComponents = combineAndFilterComponents(
        allowedComponentsFromFolders,
        allowedComponentsFromPermissions,
        blockedIds.length > 0,
      )

      return uniqBy(
        [...combinedComponents, ...allowedRootComponents.value],
        'id',
      )
    }

    return allowedComponentsFromPermissions
  })

  const allowedRootComponents = computed((): Component[] => {
    return filterByPermissions(
      rootComponents.value,
      'managed_component_ids',
      'blocked_manage_component_ids',
      'managed_component_group_uuids',
    )
  })

  const allowedComponentFolders = computed((): ComponentGroup[] => {
    if (canApplyToBlockSubfolders) {
      const effectiveAllowedIds = [...allowedIds]
      const effectiveBlockedIds = [...blockedIds]

      // Find all subfolders for allowed items
      if (allowedIds.length > 0) {
        const subfolderIds = findAllSubfolders(
          allowedIds,
          componentFolders.value,
        )
        effectiveAllowedIds.push(...subfolderIds)
      }

      // Find all subfolders for blocked items
      if (blockedIds.length > 0) {
        const subfolderIds = findAllSubfolders(
          blockedIds,
          componentFolders.value,
        )
        effectiveBlockedIds.push(...subfolderIds)
      }

      if (effectiveAllowedIds.length > 0 && effectiveBlockedIds.length > 0) {
        return componentFolders.value.filter((folder) => {
          const identifier = folder.uuid
          return (
            effectiveAllowedIds.some(
              (allowedId) => String(allowedId) === String(identifier),
            ) &&
            !effectiveBlockedIds.some(
              (blockedId) => String(blockedId) === String(identifier),
            )
          )
        })
      }

      if (effectiveAllowedIds.length > 0) {
        return componentFolders.value.filter((folder) => {
          const identifier = folder.uuid
          return effectiveAllowedIds.some(
            (allowedId) => String(allowedId) === String(identifier),
          )
        })
      }

      if (effectiveBlockedIds.length > 0) {
        return componentFolders.value.filter((folder) => {
          const identifier = folder.uuid
          return !effectiveBlockedIds.some(
            (blockedId) => String(blockedId) === String(identifier),
          )
        })
      }
    }

    return filterByPermissions(
      componentFolders.value,
      'managed_component_group_uuids',
      'blocked_manage_component_group_uuids',
      'managed_component_ids',
    )
  })

  const allowedGroupedComponents = computed((): Component[] => {
    const hasAllowedComponents =
      filterByPermissions(
        components.value,
        'managed_component_ids',
        'blocked_manage_component_ids',
        'managed_component_group_uuids',
      ).length > 0

    const allowedGroupedComponentsFromPermissions = filterByPermissions(
      groupedComponents.value,
      'managed_component_ids',
      'blocked_manage_component_ids',
      undefined,
      !hasAllowedComponents,
    )

    if (canApplyToBlockSubfolders) {
      const allowedGroupedComponentsFromFolders =
        groupedComponents.value.filter((component) =>
          allowedComponentFolders.value.some(
            (folder) => component.component_group_uuid === folder.uuid,
          ),
        )

      return uniqBy(
        combineAndFilterComponents(
          allowedGroupedComponentsFromFolders,
          allowedGroupedComponentsFromPermissions,
          blockedIds.length > 0,
        ),
        'id',
      )
    }

    return allowedGroupedComponentsFromPermissions
  })

  const allowedFoldersByEditorPermissions = computed((): ComponentGroup[] => {
    const hasAllowedRestriction =
      spaceStore?.userPermissions?.component_group_uuids?.length > 0
    const hasBlockedRestriction =
      spaceStore?.userPermissions?.blocked_component_group_uuids?.length > 0

    if (hasAllowedRestriction) {
      return componentFolders.value.filter((folder) =>
        spaceStore.userPermissions.component_group_uuids.includes(folder.uuid),
      )
    }

    if (hasBlockedRestriction) {
      return componentFolders.value.filter(
        (folder) =>
          !spaceStore.userPermissions.blocked_component_group_uuids.includes(
            folder.uuid,
          ),
      )
    }

    return componentFolders.value
  })

  return {
    allowedComponents,
    allowedRootComponents,
    allowedComponentFolders,
    allowedGroupedComponents,
    allowedFoldersByEditorPermissions,
  }
}
